// src/modules/app-views/dataSearch/pages/Search/index.jsx
// *******************************************************************************
// DATA SEARCH PAGE - Search for CPF/CNPJ data with enrichment
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Card, Grid } from 'antd';
import { useNavigate } from 'react-router-dom';
import { TEMPLATE } from 'constants/ThemeConstant';
import { API } from 'constants/Api';
import useZustand from 'hooks/useZustand';
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import marketplaceService from 'services/marketplaceService';
import entitiesService from 'services/entitiesService';
import dataSearchService from 'services/dataSearchService';
import translateX from 'utils/translateX';
import { buildUserMessageContent, buildErrorDetails } from './helpers';
import WelcomeScreen from './components/WelcomeScreen';
import EmptyState from './components/EmptyState';
import ChatMode from './components/ChatMode';
import BackToHomeLink from 'components/core/BackToHomeLink';

const { useBreakpoint } = Grid;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// PageHeader with style='default' visible area
const PAGE_HEADER_HEIGHT = 55;

// Search engines configuration
// Each engine has specific services it can search for
// Provider names: hod=jarvisly, chokmah=iwebInfo, metatron=vigidata
const SEARCH_ENGINES = [
  {
    value: 'hod',
    label: 'HOD',
    text: 'HOD',
    provider: 'jarvisly',
    online: true, // Only HOD is currently online
    services: ['cnpj'],
    stars: 2,
    placeholder: 'search_placeholder_cnpj',
  },
  {
    value: 'chokmah',
    label: 'Chokmah',
    text: 'Chokmah',
    provider: 'iwebInfo',
    online: false, // Offline - disable search button
    services: ['cnpj', 'cpf'],
    stars: 3,
    placeholder: 'search_placeholder_cnpj_cpf',
  },
  {
    value: 'metatron',
    label: 'Metatron',
    text: 'Metatron',
    provider: 'vigidata',
    online: false, // Offline - disable search button
    services: ['cpf', 'cnpj', 'name', 'phone', 'email'],
    stars: 5,
    placeholder: 'search_placeholder_full',
  },
];

// Search types per engine (derived from services)
// The first option in each array is the default for that engine
// NOTE: labelKey is the translation key - will be translated inside the component
const SEARCH_TYPES_BY_ENGINE = {
  hod: [{ value: 'cnpj', labelKey: 'cnpj' }],
  chokmah: [
    { value: 'cpf', labelKey: 'cpf' },
    { value: 'cnpj', labelKey: 'cnpj' },
  ],
  metatron: [
    { value: 'name', labelKey: 'name' },
    { value: 'cpf', labelKey: 'cpf' },
    { value: 'cnpj', labelKey: 'cnpj' },
    { value: 'email', labelKey: 'email' },
    { value: 'phone', labelKey: 'telephone' },
  ],
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************

const DataSearchSearch = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // Get api from global zustand store (done() comes from useApiInitializer)
  const apiGlobal = useZustand('global', 'useApi');
  const navType = useZustand('theme', 'navType');
  const storedParameters = useZustand('app', 'parameters');
  const storedLanguage = useZustand('theme', 'language'); // Subscribe to language changes for reactive translations
  const setPageStore = useZustand('module', 'setPage');
  const setLoadingPageStore = useZustand('boot', 'setLoadingPage');
  const setSettingsDrawerOpen = useZustand('theme', 'setSettingsDrawerOpen', false);
  const storedSubscription = useZustand('auth', 'subscription');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimerRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const chatContainerRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Detect mobile (breakpoint md = 768px)
  const isMobile = !screens.md;

  // Get the correct content gutter based on screen size
  const contentGutter = isMobile
    ? TEMPLATE.LAYOUT_CONTENT_GUTTER_SM
    : TEMPLATE.LAYOUT_CONTENT_GUTTER;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showInputMode, setShowInputMode] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedEngine, setSelectedEngine] = useState('hod');
  const [selectedSearchType, setSelectedSearchType] = useState('cnpj');
  const [lastFormValues, setLastFormValues] = useState(null);

  // Chat mode state
  const [messages, setMessages] = useState([]);
  const [searchedEntityId, setSearchedEntityId] = useState(null);
  const [typingProgress, setTypingProgress] = useState({});
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [introTypingComplete, setIntroTypingComplete] = useState(false);

  // Credits state
  const [creditsBalance, setCreditsBalance] = useState(null);
  const [isLoadingCredits, setIsLoadingCredits] = useState(false);
  const [enrichmentPrices, setEnrichmentPrices] = useState(null);
  const [apiSearchEngines, setApiSearchEngines] = useState(null);

  // DataSearch state (for linking to entities)
  const [currentDataSearchId, setCurrentDataSearchId] = useState(null);
  const [searchDataAge, setSearchDataAge] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS I
  // ─────────────────────────────────────────────────────────────────────────────

  // Hide loading page when component is mounted (autoFetch: false page)
  // When page uses DocumentProvider with autoFetch: false, we must call done() manually
  // to signal that the page is ready and hide SimpleLoadingPage
  useEffect(() => {
    if (apiGlobal?.done) {
      apiGlobal.done(true);
    }
  }, [apiGlobal?.done]);

  // Fetch credits balance and prices on mount
  const fetchCreditsBalance = useCallback(async () => {
    setIsLoadingCredits(true);
    try {
      const result = await marketplaceService.refreshCreditsBalance();
      if (result?.success) {
        setCreditsBalance(result.balance || 0);
      }
    } catch (error) {
      console.error('[DataSearch] Error fetching credits balance:', error);
    } finally {
      setIsLoadingCredits(false);
    }
  }, []);

  const fetchEnrichmentPrices = useCallback(async () => {
    try {
      const [response] = await axiosService.get({
        url: API.MIGRATION.PRICES,
        returnAsObject: false,
      });
      // Backend returns array: [{ prices: {...}, searchEngines: [...], meta: {...} }]
      // Get first element if array, then extract data
      const data = Array.isArray(response) ? response[0] : response;
      if (data?.prices) {
        setEnrichmentPrices(data.prices);
      }
      if (data?.searchEngines) {
        setApiSearchEngines(data.searchEngines);
      }
    } catch (error) {
      console.error('[DataSearch] Error fetching enrichment prices:', error);
    }
  }, []);

  // Track subscription credits to detect changes (only refresh when credits actually change)
  const lastCreditsRef = useRef(null);

  useEffect(() => {
    fetchCreditsBalance();
    fetchEnrichmentPrices();
  }, [fetchCreditsBalance, fetchEnrichmentPrices]);

  // Refresh credits only when subscription credits actually change (e.g., after purchasing)
  // This avoids unnecessary refreshes when drawer closes without purchase
  useEffect(() => {
    const currentCredits = storedSubscription?.jarvislyCredits?.balance;
    if (
      currentCredits !== undefined &&
      lastCreditsRef.current !== null &&
      currentCredits !== lastCreditsRef.current
    ) {
      fetchCreditsBalance();
    }
    lastCreditsRef.current = currentCredits;
  }, [storedSubscription?.jarvislyCredits?.balance, fetchCreditsBalance]);

  // Load saved preferences on mount
  useEffect(() => {
    const savedPreferences = appService.getModulePreferences({ category: 'dataSearch' });
    if (savedPreferences) {
      if (
        savedPreferences.engine &&
        SEARCH_ENGINES.find(e => e.value === savedPreferences.engine)
      ) {
        setSelectedEngine(savedPreferences.engine);
      }
      if (savedPreferences.searchType) {
        // Validate that searchType is valid for the engine
        const types = SEARCH_TYPES_BY_ENGINE[savedPreferences.engine || selectedEngine] || [];
        if (types.find(t => t.value === savedPreferences.searchType)) {
          setSelectedSearchType(savedPreferences.searchType);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Update search type to default when engine changes
  // Each engine has its own default (first option in the array)
  // HOD → CNPJ, Chokmah → CPF, Metatron → Nome
  const prevEngineRef = useRef(selectedEngine);
  useEffect(() => {
    // Only change if engine actually changed (not on initial mount with same value)
    if (prevEngineRef.current !== selectedEngine) {
      const types = SEARCH_TYPES_BY_ENGINE[selectedEngine] || [];
      if (types.length > 0) {
        // Always set to engine's default (first option) when engine changes
        setSelectedSearchType(types[0].value);
      }
      prevEngineRef.current = selectedEngine;
    }
  }, [selectedEngine]);

  // Scroll chat container to bottom when messages change (not the window)
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (typingTimerRef.current) {
        clearInterval(typingTimerRef.current);
      }
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
      }
    };
  }, []);

  // Calculate container height based on navigation type and screen size
  const containerHeight = useMemo(() => {
    const topNavHeight = navType === 'top' && !isMobile ? TEMPLATE.TOP_NAV_HEIGHT : 0;

    const totalOffset =
      TEMPLATE.HEADER_HEIGHT +
      topNavHeight +
      TEMPLATE.FOOTER_HEIGHT +
      contentGutter * 2 +
      PAGE_HEADER_HEIGHT;

    return `calc(100vh - ${totalOffset + 30}px)`;
  }, [navType, isMobile, contentGutter]);

  // Merge local SEARCH_ENGINES config with API data (online status, prices)
  const mergedSearchEngines = useMemo(() => {
    return SEARCH_ENGINES.map(localEngine => {
      const apiEngine = apiSearchEngines?.find(e => e.id === localEngine.value);
      if (apiEngine) {
        // Merge: keep local placeholder, use API online status and prices
        return {
          ...localEngine,
          online: apiEngine.online,
          apiPrices: apiEngine.prices, // Store API prices for later use
        };
      }
      return localEngine;
    });
  }, [apiSearchEngines]);

  // Get current selected engine config (merged)
  const selectedEngineConfig = useMemo(
    () => mergedSearchEngines.find(e => e.value === selectedEngine),
    [mergedSearchEngines, selectedEngine]
  );

  // Calculate credits info based on selected engine and search type
  const creditsInfo = useMemo(() => {
    // Use API prices from the selected engine if available
    const apiPrices = selectedEngineConfig?.apiPrices;

    // Default: no price if API not loaded
    if (!apiPrices) {
      return {
        priceBRL: 0,
        requiredCredits: 0,
        minRequiredCredits: 0,
        availableCredits: creditsBalance || 0,
        hasSufficientCredits: true,
        isLoading: isLoadingCredits,
      };
    }

    // Get price for the selected search type from the engine's API prices
    const servicePrice = apiPrices[selectedSearchType];
    const priceBRL = servicePrice?.priceBRL || 0;

    // Calculate minimum price across all services of this engine (for "a partir de" display)
    const allPrices = Object.values(apiPrices)
      .map(p => p?.priceBRL || 0)
      .filter(p => p > 0);
    const minPriceBRL = allPrices.length > 0 ? Math.min(...allPrices) : 0;

    // Convert BRL to credits (1 credit = R$ 0.10)
    const requiredCredits = Math.ceil(priceBRL * 10);
    const minRequiredCredits = Math.ceil(minPriceBRL * 10);
    const hasSufficientCredits = creditsBalance === null || creditsBalance >= requiredCredits;

    return {
      priceBRL,
      requiredCredits,
      minRequiredCredits,
      availableCredits: creditsBalance || 0,
      hasSufficientCredits,
      isLoading: isLoadingCredits,
    };
  }, [selectedEngineConfig, selectedSearchType, creditsBalance, isLoadingCredits]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  const handleStartSearch = useCallback(() => {
    setShowInputMode(true);
    setTimeout(() => {
      formRef.current?.goFocus('searchValue');
    }, 100);
  }, []);

  const handleBackToWelcome = useCallback(() => {
    // Clear timers if running
    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
    setShowInputMode(false);
    setMessages([]);
    setSearchedEntityId(null);
    setLastFormValues(null);
    setTypingProgress({});
    setElapsedSeconds(0);
    setIntroTypingComplete(false);
    // Reset dataSearch state
    setCurrentDataSearchId(null);
    setSearchDataAge(null);
  }, []);

  const handleGoBack = useCallback(() => {
    setLoadingPageStore({
      visible: true,
      extendedDelayToHide: 200,
      message: translateX('redirecting'),
    });
    setPageStore({
      navigate,
      pageName: 'historic',
    });
  }, [navigate, setPageStore, setLoadingPageStore]);

  const handleViewDocument = useCallback(
    (documentId = 'add') => {
      setLoadingPageStore({
        visible: true,
        extendedDelayToHide: 400,
      });

      setPageStore({
        navigate,
        pageName: 'document',
        params: { _id: documentId },
      });
    },
    [navigate, setLoadingPageStore, setPageStore]
  );

  // Handler for updating existing entity with new API data
  const handleUpdateEntity = useCallback(
    async (existingEntityId, newEntityData) => {
      if (!existingEntityId || !newEntityData) {
        console.error('[DataSearch] Missing entity ID or data for update');
        return;
      }

      try {
        // Build update payload using centralized service
        const updatePayload = dataSearchService.buildEntityUpdatePayload(newEntityData);

        // Update via regular PUT - backend will handle smart merge
        if (Object.keys(updatePayload).length > 0) {
          const baseUrl = API.ENTITIES.URL.replace('/:_id?', '');
          const url = `${baseUrl}/${existingEntityId}`;
          await axiosService.put({ url, data: updatePayload });
        }

        // Set the entity ID so the view button works
        setSearchedEntityId(existingEntityId);

        // Link dataSearch to entity (update syncStatus to 'synced')
        if (currentDataSearchId) {
          await dataSearchService.linkToEntity({
            _id: currentDataSearchId,
            entityId: existingEntityId,
            syncStatus: 'synced',
          });
        }

        appService.message({
          flag: 'success',
          text: 'record_updated_successfully',
        });

        console.log('[DataSearch] Entity updated successfully:', existingEntityId);
      } catch (error) {
        console.error('[DataSearch] Error updating entity:', error);
        appService.notification({
          flag: 'error',
          title: 'error_updating_entity',
          description: error?.message || translateX('unexpected_error_occurred'),
        });
        throw error;
      }
    },
    [currentDataSearchId]
  );

  // Handler for creating new entity from search data
  const handleCreateEntity = useCallback(
    async (entityData, profiles) => {
      if (!entityData) {
        console.error('[DataSearch] Missing entity data for create');
        return;
      }

      try {
        // Map 'searched' to 'search' (schema enum uses 'search')
        const mappedProfiles = profiles.map(p => (p === 'searched' ? 'search' : p));

        // Build create payload using centralized service
        const createPayload = dataSearchService.buildEntityCreatePayload(
          entityData,
          mappedProfiles
        );

        // POST to entities API (remove :_id? placeholder from URL)
        const baseUrl = API.ENTITIES.URL.replace('/:_id?', '');
        const [response] = await axiosService.post({
          url: baseUrl,
          data: createPayload,
          returnAsObject: false,
        });

        const result = Array.isArray(response) ? response[0] : response;
        const newEntityId = result?._id || result?.id;

        if (newEntityId) {
          setSearchedEntityId(newEntityId);

          // Link dataSearch to the new entity (update syncStatus to 'synced')
          if (currentDataSearchId) {
            await dataSearchService.linkToEntity({
              _id: currentDataSearchId,
              entityId: newEntityId,
              syncStatus: 'synced',
            });
          }
        }

        appService.message({
          flag: 'success',
          text: 'record_created_successfully',
        });

        console.log('[DataSearch] Entity created successfully:', newEntityId);
      } catch (error) {
        console.error('[DataSearch] Error creating entity:', error);
        appService.notification({
          flag: 'error',
          title: 'error_creating_entity',
          description: error?.message || translateX('unexpected_error_occurred'),
        });
        throw error;
      }
    },
    [currentDataSearchId]
  );

  const handleOpenCreditsDrawer = useCallback(() => {
    setSettingsDrawerOpen(true);
  }, [setSettingsDrawerOpen]);

  // Called when intro typing finishes
  const handleIntroComplete = useCallback(() => {
    setIntroTypingComplete(true);

    // Define the order of sections to reveal
    const sectionsToReveal = [
      'header',
      'basicData',
      'contact',
      'activity',
      'secondaryActivities',
      'fiscalData',
      'partners',
      'meta',
      'editLink',
    ];

    let currentIndex = 0;

    // Reveal sections progressively
    typingTimerRef.current = setInterval(() => {
      if (currentIndex < sectionsToReveal.length) {
        const section = sectionsToReveal[currentIndex];
        setTypingProgress(prev => ({
          ...prev,
          [section]: true,
        }));

        // Auto-scroll when sections appear
        if (chatContainerRef?.current) {
          setTimeout(() => {
            chatContainerRef.current.scrollTo({
              top: chatContainerRef.current.scrollHeight,
              behavior: 'smooth',
            });
          }, 50);
        }

        currentIndex++;
      } else {
        if (typingTimerRef.current) {
          clearInterval(typingTimerRef.current);
          typingTimerRef.current = null;
        }
      }
    }, 200);
  }, []);

  // Typing effect for data reveal
  const startTypingEffect = useCallback(() => {
    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
    }
    setTypingProgress({});
    setIntroTypingComplete(false);
  }, []);

  const handleEngineChange = useCallback(
    value => {
      // Find engine in merged list (has API online status)
      const engine = mergedSearchEngines.find(e => e.value === value);

      // Show warning if engine is offline
      if (engine && !engine.online) {
        appService.notification({
          flag: 'warning',
          title: 'engine_offline',
          description: 'engine_offline_description',
        });
      }

      setSelectedEngine(value);
    },
    [mergedSearchEngines]
  );

  const handleSearchTypeChange = useCallback(value => {
    setSelectedSearchType(value);
  }, []);

  const handleSearch = useCallback(
    async searchValue => {
      if (!searchValue?.trim()) {
        await appService.message({
          flag: 'w',
          text: translateX('fill_search_value'),
        });
        return;
      }

      // Check credits before searching
      if (!creditsInfo.hasSufficientCredits) {
        appService.notification({
          flag: 'warning',
          title: 'insufficient_credits',
          description: translateX('insufficient_credits_description', {
            required: creditsInfo.requiredCredits,
            available: creditsInfo.availableCredits,
          }),
        });
        return;
      }

      // Save user preferences (engine and searchType) for next time
      appService.updateModulePreferences({
        category: 'dataSearch',
        data: {
          engine: selectedEngine,
          searchType: selectedSearchType,
        },
      });

      // Build user message content
      const userMessageContent = buildUserMessageContent({ searchValue }, selectedEngine);

      // Add user message
      const userMessage = {
        role: 'user',
        content: userMessageContent,
        data: { searchValue, engine: selectedEngine },
      };

      // Add assistant thinking message
      const assistantMessage = {
        role: 'assistant',
        content: '',
        status: 'thinking',
      };

      setMessages(prev => [...prev, userMessage, assistantMessage]);
      setLastFormValues({ searchValue });
      setIsProcessing(true);
      setSearchedEntityId(null);
      // Reset dataSearch state for new search
      setCurrentDataSearchId(null);
      setSearchDataAge(null);

      // Start elapsed time counter
      setElapsedSeconds(0);
      elapsedTimerRef.current = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);

      try {
        // Call backend data-search endpoint (handles credit debit)
        const [response] = await axiosService.post({
          url: API.DATA_SEARCH.SEARCH,
          data: {
            engine: selectedEngine,
            serviceType: selectedSearchType,
            searchValue: searchValue.replace(/\D/g, ''), // Clean value for CNPJ/CPF
          },
          returnAsObject: false,
        });

        // Backend returns array via routerService, get first element if needed
        const result = Array.isArray(response) ? response[0] : response;

        console.log('[DataSearch] Search result:', result);

        if (result?.success && result?.data?.success) {
          const entityData = result.data;
          const resolvedProvider = entityData.provider || result.engine;

          // Use entity existence check from backend response
          const entityExists = result.entityExists || false;
          const existingEntityId = result.existingEntityId || null;
          const existingEntityProfile = result.existingEntityProfile || null;

          // Set the searchedEntityId for existing entities or new ones with _id
          if (existingEntityId) {
            setSearchedEntityId(existingEntityId);
          } else if (entityData?._id) {
            setSearchedEntityId(entityData._id);
          }

          // Store dataSearch document info for linking
          if (result.dataSearchId) {
            setCurrentDataSearchId(result.dataSearchId);
          }
          if (result.dataSearchAge !== null && result.dataSearchAge !== undefined) {
            setSearchDataAge(result.dataSearchAge);
          }

          // Update assistant message with success
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = {
              role: 'assistant',
              content: entityExists
                ? translateX('entity_already_exists')
                : translateX('entity_found_successfully'),
              status: 'success',
              entity: entityData,
              engine: selectedEngine,
              resolvedProvider,
              entityExists,
              existingEntityId,
              existingEntityProfile,
              meta: {
                responseTimeMs: result.responseTimeMs || 0,
                creditsUsed: result._creditsDebit?.debited || creditsInfo.requiredCredits,
              },
            };
            return updated;
          });

          // Refresh credits after successful search
          fetchCreditsBalance();

          // Start typing effect for the entity data
          startTypingEffect();

          // Clear form for new search
          setLastFormValues(null);
          // For phone type, use nested structure matching SimplePhone's field name
          const emptySearchValue =
            selectedSearchType === 'phone' ? { searchValue: { value: '' } } : { searchValue: '' };
          formRef.current?.form?.setFieldsValue(emptySearchValue);

          // Show credits debit notification (from backend response)
          const creditsDebit = result._creditsDebit;
          if (creditsDebit?.debited > 0) {
            appService.notification({
              flag: 'info',
              title: 'credits_debited',
              description: translateX('credits_debited_description', {
                debited: creditsDebit.debited,
                operation: translateX('data_search'),
                balance: creditsDebit.balanceAfter,
              }),
            });
          }
        } else {
          // Update assistant message with error
          const errorDetails = buildErrorDetails({
            message: result?.error || result?.data?.error || translateX('search_failed'),
            code: result?.errorCode || 'SEARCH_FAILED',
          });
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = {
              role: 'assistant',
              status: 'error',
              errorDetails,
            };
            return updated;
          });
        }
      } catch (error) {
        console.error('[DataSearch] Search error:', error);
        const errorDetails = buildErrorDetails({
          message: error?.response?.data?.message || error?.message,
          code: error?.response?.data?.code || error?.code,
          statusCode: error?.response?.status,
          isNetworkError: !error?.response,
        });
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            role: 'assistant',
            status: 'error',
            errorDetails,
          };
          return updated;
        });
      } finally {
        // Stop elapsed time counter
        if (elapsedTimerRef.current) {
          clearInterval(elapsedTimerRef.current);
          elapsedTimerRef.current = null;
        }
        setIsProcessing(false);
        formRef.current?.goFocus('searchValue');
      }
    },
    [selectedEngine, selectedSearchType, creditsInfo, fetchCreditsBalance, startTypingEffect]
  );

  // Handler for form submission from InputForm/EmptyState
  const handleFormFinish = useCallback(
    async ({ values }) => {
      let { searchValue } = values;

      // For phone type, searchValue is an object with 'phone' or 'value' property
      if (selectedSearchType === 'phone' && typeof searchValue === 'object') {
        searchValue = searchValue.phone || searchValue.value || '';
      }

      // Scroll page to top so user can see the chat response
      window.scrollTo({ top: 0, behavior: 'smooth' });

      await handleSearch(searchValue);
    },
    [handleSearch, selectedSearchType]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // Build search type options with translated labels (reactive to language changes)
  const searchTypeOptions = useMemo(() => {
    const types = SEARCH_TYPES_BY_ENGINE[selectedEngine] || [];
    return types.map(type => ({
      value: type.value,
      label: translateX(type.labelKey),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEngine, storedLanguage]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. EFFECTS II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────

  const renderContent = () => {
    if (!showInputMode) {
      return (
        <WelcomeScreen
          isMobile={isMobile}
          onStartSearch={handleStartSearch}
          onGoBack={handleGoBack}
        />
      );
    }

    // If we have messages, show chat mode
    if (messages.length > 0) {
      return (
        <ChatMode
          messages={messages}
          formRef={formRef}
          isProcessing={isProcessing}
          lastFormValues={lastFormValues}
          selectedEngine={selectedEngine}
          selectedSearchType={selectedSearchType}
          searchEngines={mergedSearchEngines}
          searchTypeOptions={searchTypeOptions}
          creditsInfo={creditsInfo}
          elapsedSeconds={elapsedSeconds}
          typingProgress={typingProgress}
          introTypingComplete={introTypingComplete}
          searchedEntityId={searchedEntityId}
          searchDataAge={searchDataAge}
          chatContainerRef={chatContainerRef}
          messagesEndRef={messagesEndRef}
          isMobile={isMobile}
          onEngineChange={handleEngineChange}
          onSearchTypeChange={handleSearchTypeChange}
          onFinish={handleFormFinish}
          onBackToWelcome={handleBackToWelcome}
          onIntroComplete={handleIntroComplete}
          onUpdateEntity={handleUpdateEntity}
          onCreateEntity={handleCreateEntity}
          onOpenCreditsDrawer={handleOpenCreditsDrawer}
        />
      );
    }

    // Otherwise show EmptyState with form
    return (
      <EmptyState
        formRef={formRef}
        isProcessing={isProcessing}
        lastFormValues={lastFormValues}
        selectedEngine={selectedEngine}
        selectedSearchType={selectedSearchType}
        searchEngines={mergedSearchEngines}
        searchTypeOptions={searchTypeOptions}
        creditsInfo={creditsInfo}
        isMobile={isMobile}
        onEngineChange={handleEngineChange}
        onSearchTypeChange={handleSearchTypeChange}
        onFinish={handleFormFinish}
        onBackToWelcome={handleBackToWelcome}
        onOpenCreditsDrawer={handleOpenCreditsDrawer}
      />
    );
  };

  return (
    <div className="container mt-3">
      <Card
        styles={{
          body: {
            padding: isMobile ? '26px 16px 16px' : '26px 26px 0',
            display: 'flex',
            flexDirection: 'column',
            ...(isMobile ? {} : { height: '100%' }),
          },
        }}
        style={{
          // Desktop: fixed height with scroll, Mobile: auto height
          ...(isMobile ? {} : { height: containerHeight, overflow: 'hidden' }),
          marginBottom: isMobile ? 0 : -contentGutter - 10,
        }}
      >
        {renderContent()}

        <BackToHomeLink style={{ marginBottom: 48 }} />
      </Card>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default DataSearchSearch;

// *******************************************************************************
// HELPERS
// *******************************************************************************
